import { useCallback, useEffect, useRef, useState } from 'preact/hooks'
import { runSearch, type SearchProgress, type SearchResult, type SearchType, type SearchResultType, type CancelToken } from '../services/searchRunner'
import { askQuickChat, type QuickChatEntry } from '../services/quickChat'
import { AnswerText } from './HomeScreen'

interface Props {
  onClose: () => void
  onNavigate: (view: string, focusId?: string) => void
  /** Persona to use when running memory search; defaults to 'pipar'. */
  personaId?: string
  /** Continue a quick-chat answer in full chat, carrying the exchange as history */
  onContinueQuickChat?: (entry: QuickChatEntry) => void
  /** Open a full chat with the selected persona, sending the typed text (if any) as the first message */
  onStartChat?: (text?: string) => void
  /** Selected persona's display name, shown on the start-chat button */
  personaName?: string
}

/** Quick chat sits next to the search types: same input, but the AI answers in place. */
type DialogMode = SearchType | 'quick'

const TYPE_OPTIONS: Array<{ value: DialogMode; label: string; description: string }> = [
  { value: 'title', label: 'Title', description: 'Match keywords in titles only — fastest.' },
  { value: 'memory', label: 'Memory', description: 'Run the AI memory search tool (semantic).' },
  { value: 'content', label: 'Content', description: 'Scan all text bodies — heaviest.' },
  { value: 'quick', label: 'Quick chat', description: 'Ask the AI — answered here, can use your tools.' },
]

const TYPE_ICONS: Record<SearchResultType, string> = {
  chat: '\u{1F4AC}',
  task: '\u{2705}',
  note: '\u{1F4DD}',
  voice: '\u{1F3A4}',
  image: '\u{1F5BC}',
  document: '\u{1F4C4}',
  followup: '\u{1F4DE}',
  postpone: '\u{23F0}',
  proactive: '\u{1F50D}',
  briefing: '\u{1F305}',
  bulletin: '\u{1F4E2}',
  alert: '\u{1F6A8}',
  box: '\u{1F4E6}',
  memory: '\u{1F9E0}',
}

const TYPE_LABEL: Record<SearchResultType, string> = {
  chat: 'Chat',
  task: 'Task',
  note: 'Note',
  voice: 'Voice',
  image: 'Image',
  document: 'Document',
  followup: 'Follow-up',
  postpone: 'Postpone',
  proactive: 'Proactive',
  briefing: 'Briefing',
  bulletin: 'Bulletin',
  alert: 'Alert',
  box: 'Box',
  memory: 'Memory',
}

export function SearchDialog({ onClose, onNavigate, personaId, onContinueQuickChat, onStartChat, personaName }: Props) {
  const [query, setQuery] = useState('')
  const [searchType, setSearchType] = useState<DialogMode>('title')
  const [quickEntry, setQuickEntry] = useState<QuickChatEntry | null>(null)
  const isQuick = searchType === 'quick'
  const quickPending = quickEntry?.status === 'pending'
  const [running, setRunning] = useState(false)
  const [progress, setProgress] = useState<SearchProgress | null>(null)
  const cancelRef = useRef<CancelToken>({ cancelled: false })
  const inputRef = useRef<HTMLInputElement | null>(null)

  // Esc closes the dialog only when no search is running.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !running) onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose, running])

  // Auto-focus the input on mount.
  useEffect(() => { inputRef.current?.focus() }, [])

  // If the dialog unmounts while a search is in flight, cancel it.
  useEffect(() => () => { cancelRef.current.cancelled = true }, [])

  const handleStart = useCallback(async () => {
    if (!query.trim() || running) return
    if (searchType === 'quick') {
      if (quickPending) return
      // The first onChange is synchronous and carries the new pending entry;
      // follow that id as the answer streams in. The answer keeps landing in the
      // stored quick-chat list even if the dialog closes meanwhile.
      let entryId: string | null = null
      askQuickChat(query.trim(), (entries) => {
        entryId ??= entries[0]?.id ?? null
        const e = entries.find((x) => x.id === entryId)
        if (e) setQuickEntry(e)
      })
      return
    }
    setQuickEntry(null)
    cancelRef.current = { cancelled: false }
    setRunning(true)
    setProgress({ scanning: 'Starting…', scanned: 0, total: 0, results: [], done: false })
    try {
      await runSearch({
        query: query.trim(),
        type: searchType,
        personaId,
        signal: cancelRef.current,
        onProgress: (p) => setProgress(p),
      })
    } finally {
      setRunning(false)
    }
  }, [query, searchType, running, personaId, quickPending])

  const handleStop = useCallback(() => {
    cancelRef.current.cancelled = true
  }, [])

  const handleResultClick = (r: SearchResult) => {
    // Memory results don't have a navigable detail page yet — just navigate without focus.
    onNavigate(r.view, r.type === 'memory' ? undefined : r.id)
    onClose()
  }

  const totalCount = progress?.results.length ?? 0
  const pct = progress && progress.total > 0
    ? Math.min(100, Math.round((progress.scanned / progress.total) * 100))
    : null

  return (
    <div
      onClick={() => { if (!running) onClose() }}
      style={{
        position: 'fixed',
        top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0,0,0,0.6)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '8vh 20px 20px',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--bg)',
          border: '1px solid var(--border)',
          borderRadius: 12,
          width: '100%',
          maxWidth: 720,
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 18 }}>{'\u{1F50D}'}</span>
          <input
            ref={(el) => { inputRef.current = el as HTMLInputElement | null }}
            type="text"
            value={query}
            onInput={(e) => setQuery((e.target as HTMLInputElement).value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !running) handleStart()
            }}
            placeholder="Search…"
            disabled={running}
            style={{
              flex: 1,
              padding: '10px 12px',
              fontSize: 16,
              background: 'var(--bg-surface)',
              border: '1px solid var(--border)',
              borderRadius: 8,
              color: 'var(--text)',
              outline: 'none',
            }}
          />
          <button
            onClick={onClose}
            disabled={running}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--text-muted)',
              cursor: running ? 'default' : 'pointer',
              fontSize: 22,
              lineHeight: 1,
              padding: 4,
              opacity: running ? 0.4 : 1,
            }}
            title="Close (Esc)"
          >
            {'\u{00D7}'}
          </button>
        </div>

        {/* Type selector */}
        <div style={{ padding: '12px 18px', borderBottom: '1px solid var(--border)' }}>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Search type
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {TYPE_OPTIONS.map((opt) => {
              const selected = searchType === opt.value
              return (
                <button
                  key={opt.value}
                  onClick={() => !running && setSearchType(opt.value)}
                  disabled={running}
                  title={opt.description}
                  style={{
                    flex: '1 1 160px',
                    padding: '10px 14px',
                    borderRadius: 8,
                    border: selected ? '2px solid var(--primary)' : '1px solid var(--border)',
                    background: selected ? 'var(--assistant-bubble)' : 'var(--bg-surface)',
                    color: 'var(--text)',
                    cursor: running ? 'default' : 'pointer',
                    fontSize: 13,
                    textAlign: 'left',
                    opacity: running ? 0.6 : 1,
                  }}
                >
                  <div style={{ fontWeight: 600, marginBottom: 2 }}>{opt.label}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{opt.description}</div>
                </button>
              )
            })}
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'center' }}>
            <button
              onClick={handleStart}
              disabled={!query.trim() || running || (isQuick && quickPending)}
              style={{
                padding: '10px 22px',
                borderRadius: 8,
                border: 'none',
                background: 'var(--primary)',
                color: '#fff',
                cursor: (!query.trim() || running || (isQuick && quickPending)) ? 'default' : 'pointer',
                fontSize: 14,
                fontWeight: 600,
                opacity: (!query.trim() || running || (isQuick && quickPending)) ? 0.6 : 1,
              }}
            >
              {isQuick ? (quickPending ? 'Answering…' : 'Ask') : running ? 'Searching…' : 'Search'}
            </button>
            {running && (
              <button
                onClick={handleStop}
                style={{
                  padding: '10px 18px',
                  borderRadius: 8,
                  border: '1px solid #f44336',
                  background: 'transparent',
                  color: '#f44336',
                  cursor: 'pointer',
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                Stop
              </button>
            )}
            <span style={{ flex: 1 }} />
            {progress && !isQuick && (
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                {totalCount} match{totalCount === 1 ? '' : 'es'}
              </span>
            )}
            {onStartChat && (
              <button
                onClick={() => { onStartChat(query.trim() || undefined); onClose() }}
                disabled={running}
                title={query.trim() ? 'Open chat and send the text above as the first message' : 'Open a chat window'}
                style={{
                  padding: '10px 18px',
                  borderRadius: 8,
                  border: '1px solid var(--primary)',
                  background: 'transparent',
                  color: 'var(--primary)',
                  cursor: running ? 'default' : 'pointer',
                  fontSize: 14,
                  fontWeight: 600,
                  opacity: running ? 0.6 : 1,
                }}
              >
                {'\u{1F4AC}'} Start chat{personaName ? ` with ${personaName}` : ''}
              </button>
            )}
          </div>
        </div>

        {/* Quick chat answer */}
        {isQuick && quickEntry && (
          <div style={{ flex: 1, overflow: 'auto', padding: '12px 18px', display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--primary)', textTransform: 'uppercase', letterSpacing: 0.4 }}>
              {quickPending
                ? quickEntry.tools[quickEntry.tools.length - 1] || 'Writing…'
                : quickEntry.status === 'error' ? 'Answer failed' : 'Answer'}
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{quickEntry.question}</div>
            {quickEntry.answer.trim()
              ? <AnswerText text={quickEntry.answer} />
              : quickPending && <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Waiting for the answer…</div>}
            {quickEntry.status === 'error' && <div style={{ fontSize: 13, color: '#f44336' }}>{quickEntry.error}</div>}
            {quickEntry.changedData && !quickPending && <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Your data was updated.</div>}
            {onContinueQuickChat && !quickPending && (
              <div>
                <button
                  onClick={() => { onContinueQuickChat(quickEntry); onClose() }}
                  style={{
                    padding: '8px 16px',
                    borderRadius: 8,
                    border: '1px solid var(--primary)',
                    background: 'transparent',
                    color: 'var(--primary)',
                    cursor: 'pointer',
                    fontSize: 13,
                    fontWeight: 600,
                  }}
                >
                  Continue in chat
                </button>
              </div>
            )}
          </div>
        )}

        {/* Progress + results */}
        {progress && !isQuick && (
          <>
            <div style={{ padding: '8px 18px', borderBottom: '1px solid var(--border)', fontSize: 12, color: 'var(--text-muted)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ flex: 1 }}>
                  {progress.error ? <span style={{ color: '#f44336' }}>{progress.error}</span> : progress.scanning}
                  {progress.total > 0 && ` — ${progress.scanned} / ${progress.total}`}
                </span>
                {pct !== null && (
                  <span style={{ fontSize: 11 }}>{pct}%</span>
                )}
              </div>
              {pct !== null && (
                <div style={{ width: '100%', height: 4, background: 'var(--border)', borderRadius: 2, marginTop: 6, overflow: 'hidden' }}>
                  <div style={{ width: `${pct}%`, height: '100%', background: 'var(--primary)', transition: 'width 0.15s' }} />
                </div>
              )}
            </div>

            <div style={{ flex: 1, overflow: 'auto' }}>
              {progress.results.length === 0 && progress.done && !progress.error && (
                <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
                  No matches found.
                </div>
              )}
              {progress.results.map((r) => (
                <button
                  key={`${r.type}-${r.id}`}
                  onClick={() => handleResultClick(r)}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    padding: '12px 18px',
                    background: 'transparent',
                    border: 'none',
                    borderBottom: '1px solid var(--border)',
                    color: 'var(--text)',
                    cursor: 'pointer',
                    fontSize: 13,
                  }}
                  onMouseOver={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--bg-surface)' }}
                  onMouseOut={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent' }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <span style={{ fontSize: 14 }}>{TYPE_ICONS[r.type]}</span>
                    <span style={{
                      fontSize: 10,
                      padding: '2px 6px',
                      borderRadius: 4,
                      background: 'rgba(45,165,160,0.15)',
                      color: 'var(--primary)',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: 0.4,
                    }}>
                      {TYPE_LABEL[r.type]}
                    </span>
                    <span style={{ fontWeight: 500, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {r.title}
                    </span>
                  </div>
                  {r.snippet && (
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.4, paddingLeft: 22 }}>
                      {r.snippet}
                    </div>
                  )}
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  )
}
